<template>
  <div class="page-content prose">
    <div class="container">
      <div class="flex justify-center pb-8">
        <ContentInfoLinks />
      </div>

      <h1>U gaat zelf aan de slag?</h1>
      <p>
        Bent u van plan om zelf te gaan schilderen? Dan kunt u waarschijnlijk
        wel een aantal tips gebruiken.
      </p>

      <h2>Een goede voorbereiding is het halve werk</h2>
      <p>
        Zorg voor een goede voorbereiding voordat u daadwerkelijk aan de slag
        gaat. De voorbereiding voor het daadwerkelijke schilderen neemt maar
        liefst 80% van de tijd in beslag. Als u de stappen 1 t/m 5 volgt, bent u
        op alles voorbereid.
      </p>

      <p>
        <strong class="block"> Stap 1: Werp een kritische blik </strong>
        Voordat u begint met het verven dient u het te verven oppervlak te
        inspecteren op oneffenheden. Kijk of er scheuren/splijten te zien zijn.
        Deze kunnen houtrot tot gevolg hebben. Merk ook op als de verf begint te
        bladeren of als er blazen of kale plekken ontstaan zijn.
      </p>

      <p>
        <strong class="block">
          Stap 2: Test de staat van uw huidige schilderwerk
        </strong>
        Bekijk in welke staat uw huidige schilderwerk verkeerd. Dit kunt u zelf
        testen door op de verf een stukje tape te plakken. Trek deze er
        vervolgens in één keer eraf. Als er verf op de tape achterblijft
        betekent dit dat de huidige verflaag in slechte staat verkeerd. Het is
        aan te raden om in dat geval de oude laag verf te verwijderen.
      </p>

      <p>
        <strong class="block"> Stap 3: Verwijder het oude schilderwerk </strong>
        <span class="block">Buiten</span>
        Het oude schilderwerk aan de buitenkant van uw huis kunt u het
        makkelijkst verwijderen met een krabber en een schildersföhn. Blaas met
        de föhn hete lucht over het geschilderde oppervlak. Zodra er blazen in
        de verf komen kunt u met een krabber de verf eraf krabben. Zorg voor een
        scherpe krabber!
      </p>

      <p>
        <span class="block">Binnen</span>
        Het oude schilderwerk aan de binnenkant van uw huis kunt u het
        makkelijkst verwijderen met een afbijtmiddel en een plamuurmes. Breng
        het afbijtmiddel royaal aan, doe dit met een kwast. Laat het enige tijd
        inwerken. Hierna kunt u de verf gemakkelijk wegsteken met een
        plamuurmes. Vergeet niet om na het afbijten het houtwerk grondig te
        wassen.
      </p>

      <p>
        <strong class="block"> Stap 4: Schuren is belangrijk </strong>
        Voordat u de grondlaag gaat aanbrengen dient u nieuw en kaal houtwerk
        altijd eerst te schuren. Als de oude verflaag nog in goede staat
        verkeert dient u het zowel te schuren als te ontvetten. Dit kunt u doen
        met behulp van ammoniakwater en watervast schuurpapier.
      </p>

      <p>
        <strong class="block"> Stap 5: Herstel houtrot </strong>
        In stap 1 werd u gewezen op het feit dat houtrot kan ontstaan als er
        scheuren/splijten in uw houtwerk zitten. Na het schuren kunnen deze
        plekken pas echt goed zichtbaar worden. Er kan water in het hout komen
        als er een scheur in de verflaag is ontstaan. Hierdoor ontstaat er
        houtrot onder de verflaag. Wat kunt u hieraan doen?
      </p>

      <ul class="mb-4">
        <li>Het aangetaste hout dient u voorzichtig weg te steken;</li>
        <li>Met een houtreparatiemiddel kunt u de plek herstellen.</li>
      </ul>

      <h2>Verf kiezen?</h2>
      <p>
        Bij een goede voorbereiding, hoort goede verf. Maar wat is nou ‘goede’
        verf? Wat voor soort verf heb ik eigenlijk nodig? Wat voor kwast kan ik
        het best gebruiken? En hoeveel verf zal ik nodig hebben? Dit zijn een
        aantal problemen waar u tegenaan kunt lopen als u zelf aan de slag wilt.
        Hier volgen een aantal tips voor u:
      </p>

      <p>
        <strong class="block">
          Tip 1: Bekijk goed wát u gaat schilderen
        </strong>
        Let hierbij goed op de toestand van de ondergrond. Op wat voor materiaal
        gaat u de verf aanbrengen bijvoorbeeld? Uw verfspecialist kan u hierbij
        helpen en advies geven. Er zijn veel soorten ondergronden waarop u verf
        kunt aanbrengen. In het overzicht hieronder staat weergegeven welke
        soorten ondergronden er zoal zijn. Ook staat hierbij weergegeven welke
        voorbehandeling zij moeten hebben voordat er daadwerkelijk begonnen kan
        worden met verven.
      </p>

      <t-table class="mb-4">
        <thead>
          <tr>
            <th>Ondergrond</th>
            <th>Voorbehandeling</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Blank hout</td>
            <td>
              <ul>
                <li>Vetvrij maken (ammonia)</li>
                <li>Schuren</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Geverfd hout</td>
            <td>
              <ul>
                <li>Verflagen afkrabben</li>
                <li>Vetvrij maken (ammonia)</li>
                <li>Schuren</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Houten trap</td>
            <td>
              <ul>
                <li>Verflagen afkrabben</li>
                <li>Vetvrij maken (ammonia)</li>
                <li>Schuren</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Houten vloer</td>
            <td>
              <ul>
                <li>Vetvrij maken (ammonia)</li>
                <li>Schuren</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Ingemetseld houtwerk</td>
            <td>
              <ul>
                <li>Vetvrij maken (ammonia)</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>MDF</td>
            <td>
              <ul>
                <li>Blanke lak</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Multiplex</td>
            <td>
              <ul>
                <li>Schuren</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Gipsplaten</td>
            <td>
              <ul>
                <li>Gaten/kieren afsmeren</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Ytong blokken</td>
            <td>
              <ul>
                <li>Voorstrijk</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Kalkzandsteen</td>
            <td>
              <ul>
                <li>Voorstrijk</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Steen</td>
            <td>
              <ul>
                <li>Voorstrijk</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Glad beton</td>
            <td>
              <ul>
                <li>Geen</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Ruw beton</td>
            <td>
              <ul>
                <li>Voorstrijk</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Nieuw ijzer</td>
            <td>
              <ul>
                <li>Laten stralen</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Oud ijzer</td>
            <td>
              <ul>
                <li>Staalborstel</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Radiatoren</td>
            <td>
              <ul>
                <li>Ontvetten</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Kunststof</td>
            <td>
              <ul>
                <li>Ontvetten</li>
                <li>Schuren</li>
              </ul>
            </td>
          </tr>
        </tbody>
      </t-table>

      <p>
        <strong class="block"> Tip 2: Kies de juiste verf </strong>
        Het is belangrijk advies te vragen aan een deskundige. Vraag uw
        verfspecialist welke soorten verf er geschikt zijn voor de door u te
        beschilderen oppervlakten. Vergeet hem niet te vragen hoe het resultaat
        er uit zal zien. Vraag eventueel om een voorbeeld.
      </p>

      <p>
        In het overzicht hieronder staat weergegeven welke soorten verf geschikt
        zijn voor een bepaalde ondergrond.
      </p>

      <t-table class="mb-4">
        <thead>
          <tr>
            <th>
              <strong>Ondergrond</strong>
            </th>
            <th>
              <strong>Afwerking</strong>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Blank hout</td>
            <td>
              <ul>
                <li>Alkydverf (met oplosmiddel) voor buiten en binnen</li>
                <li>acrylverf (op waterbasis) voor binnen</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Geverfd hout</td>
            <td>
              <ul>
                <li>Alkydverf (met oplosmiddel) voor buiten en binnen</li>
                <li>acrylverf (op waterbasis) voor binnen</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Houten trap</td>
            <td>
              <ul>
                <li>Alkydverf</li>
                <li>acrylverf</li>
                <li>lak</li>
                <li>beits</li>
                <li>
                  de toplaag met slijtvaste poly-urethaan lak afwerken, minimaal
                  drie lagen.
                </li>
                <li>De laatste laag poly-urethaan anti-slip lak gebruiken.</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Houten vloer</td>
            <td>
              <ul>
                <li>Parketlak</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Ingemetseld houtwerk</td>
            <td>
              <ul>
                <li>IJzermenie</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>MDF</td>
            <td>
              <ul>
                <li>Alkydverf of</li>
                <li>Acrylverf</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Multiplex</td>
            <td>
              <ul>
                <li>Alkydverf of</li>
                <li>Acrylverf</li>
                <li>Na eerste verflaag eerst weer schuren</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Gipsplaten</td>
            <td>
              <ul>
                <li>Latex</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Ytong blokken</td>
            <td>
              <ul>
                <li>Latex</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Kalkzandsteen</td>
            <td>
              <ul>
                <li>Latex</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Steen</td>
            <td>
              <ul>
                <li>Latex</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Glad beton</td>
            <td>
              <ul>
                <li>Latex</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Ruw beton</td>
            <td>
              <ul>
                <li>Latex</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Nieuw ijzer</td>
            <td>
              <ul>
                <li>Menie</li>
                <li>+ verf of hammerite</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Oud ijzer</td>
            <td>
              <ul>
                <li>Menie</li>
                <li>+ verf of hammerite</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Radiatoren</td>
            <td>
              <ul>
                <li>
                  Radiolak<br />(voor als de radiator helder wit moet worden en
                  blijven)
                </li>
                <li>Anders alkydverf</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Kunststof</td>
            <td>
              <ul>
                <li>Kunststoflak</li>
              </ul>
            </td>
          </tr>
        </tbody>
      </t-table>

      <p>
        <strong class="block"> Tip 3: Bepaal de hoeveelheid verf </strong>
        Hoe kunt u erachter komen hoeveel verf u nodig heeft? Op het internet
        kunt u een aantal calculators raadplegen. Deze berekenen hoeveel liter
        verf u nodig zal hebben. In veel gevallen zal u moeten opgeven om wat
        voor soort ondergrond het gaat, hoeveel m2 het te beschilderen oppervlak
        bedraagt en de soort verf die u wilt gebruiken. Indien u niet over
        internetmogelijkheden beschikt kunt u op de verfpot zelf kijken. Daarop
        staat ook beschreven hoeveel m2 u ermee kunt schilderen. Natuurlijk kunt
        u de verkoper altijd om advies vragen.
      </p>

      <p>
        <strong class="block"> Tip 4: Gebruik van kwast of roller </strong>
        <span class="italic block">
          Wanneer kunt u het best gebruik maken van een kwast?
        </span>
        De kwast wordt in de meeste gevallen gebruikt voor de wat kleinere
        schilderklussen. Ook is de kwast zeer geschikt voor kleine oppervlakten
        en moeilijke plaatsen.

        <span class="italic block">
          Wanneer kunt u het best gebruik maken van een roller?
        </span>
        De roller wordt in de meeste gevallen gebruikt voor de wat grotere
        oppervlakten. Hierdoor kunt u relatief grote oppervlakten in een korte
        tijd beschilderen.
      </p>

      <p>
        In het overzicht hieronder staat weergegeven welk gereedschap u nodig
        hebt bij het verven van een bepaalde ondergrond.
      </p>

      <t-table class="mb-4">
        <tbody>
          <tr>
            <td>
              <strong>Ondergrond</strong>
            </td>
            <td>
              <strong>Gereedschap</strong>
            </td>
          </tr>
          <tr>
            <td>Blank hout</td>
            <td>
              <ul>
                <li>Kwast voor kozijnen</li>
                <li>hoekenlakroller voor vlakke deuren</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Geverfd hout</td>
            <td>
              <ul>
                <li>Kwast voor kozijnen</li>
                <li>hoekenlakroller voor vlakke deuren</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Houten trap</td>
            <td>
              <ul>
                <li>Kwast</li>
                <li>hoekenlakroller voor loopvlakken</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Houten vloer</td>
            <td>
              <ul>
                <li>Grote lakrollerkwast voor hoeken</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Ingemetseld houtwerk</td>
            <td>
              <ul>
                <li>Kwast</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>MDF</td>
            <td>
              <ul>
                <li>Lakroller</li>
                <li>Klein kwastje voor hoeken</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Multiplex</td>
            <td>
              <ul>
                <li>Lakroller</li>
                <li>Klein kwastje voor hoeken</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Gipsplaten</td>
            <td>
              <ul>
                <li>Vacht of schuimroller</li>
                <li>Grote kwast voor hoeken</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Ytong blokken</td>
            <td>
              <ul>
                <li>Vacht of schuimroller</li>
                <li>Grote kwast voor hoeken</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Kalkzandsteen</td>
            <td>
              <ul>
                <li>Vacht of schuimroller</li>
                <li>Grote kwast voor hoeken</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Steen</td>
            <td>
              <ul>
                <li>Vacht of schuimroller</li>
                <li>Grote kwast voor hoeken</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Glad beton</td>
            <td>
              <ul>
                <li>Vacht of schuimroller</li>
                <li>Grote kwast voor hoeken</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Ruw beton</td>
            <td>
              <ul>
                <li>Vacht of schuimroller</li>
                <li>Grote kwast voor hoeken</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Nieuw ijzer</td>
            <td>
              <ul>
                <li>Kwast</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Oud ijzer</td>
            <td>
              <ul>
                <li>Kwast</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Radiatoren</td>
            <td>
              <ul>
                <li>Radiatorkwast</li>
                <li>Hoekkwast</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>Kunststof</td>
            <td>
              <ul>
                <li>Lakroller</li>
              </ul>
            </td>
          </tr>
        </tbody>
      </t-table>

      <p>
        <strong class="block"> Tip 5: Afplakken </strong>
        Vergeet niet om de plaatsen naast het te beschilderen gedeelte af te
        plakken met tape. Dit voorkomt dat er verf op plaatsen komt te zitten
        waar het niet hoort. Zodra u klaar bent met verven dient u de tape
        direct te verwijderen. Anders bestaat de mogelijkheid dat de verf
        gedroogd aan de tape zit, waardoor u bij het wegtrekken alles stuk
        trekt.
      </p>

      <h2>Aan de slag!</h2>
      <p>
        Afhankelijk van waar en wat u gaat verven begint het echte werk met het
        leegmaken van ruimtes. Bijvoorbeeld als u een kamer in huis gaat verven.
        Maak de ruimte leeg of zet het meubilair aan de kant en bedek het met
        een oud laken. Vergeet niet om uw vloer te beschermen tegen eventuele
        verfspetters. Leg kranten op de vloer zodat deze niet kan beschadigen.
      </p>
      <p>
        Het is mogelijk om een oppervlakte te beschilderen waar al een andere
        verflaag/-soort opzit. Sommige verfsoorten hechten niet op elkaar. Test
        daarom voor de zekerheid of de nieuwe verf wel goed hecht aan de oude
        verf. Het beste is om dezelfde verfsoort te gebruiken.
      </p>
      <p>
        Zorg ervoor dat de verf niet te koud is. De verf dient op
        kamertemperatuur te zijn om tot een mooi resultaat te komen. Tip: zet de
        potten verf een tijd voor u gaat verven in huis zodat deze op
        kamertemperatuur kunnen komen.
      </p>
      <p>
        Zorg bij grote oppervlaktes dat je steeds in dezelfde richting
        schildert. Werk bij voorkeur steeds met horizontale banden. Het is
        belangrijk om af en toe pauze te nemen. Zorg er wel voor dat u bij het
        verven van een muur, de muur eerst helemaal beschildert voordat u pauze
        gaat houden. Als u dit niet doet kunnen er kleurverschillen ontstaan.
      </p>
    </div>
  </div>
</template>

<script>
import AbstractPage from 'chimera/all/components/page/AbstractPage'
import ContentInfoLinks from '~/components/layout/ContentInfoLinks'

export default {
  components: {
    ContentInfoLinks,
  },

  extends: AbstractPage,

  layout: 'content',

  /**
   * @returns {object}
   */
  data() {
    return {
      headTitle: 'Informatie over schildersbedrijven?',
      headDescription:
        'Meer informatie over Schildervergelijker.nl vind je op deze informatie pagina.',
      path: '/schilder-informatie/zelf-schilderen',
    }
  },
}
</script>
